import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, Loader2, Trash2, ExternalLink, ShieldCheck } from "lucide-react";

const dataBreve = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

// Trova i file con contenuto identico nelle cartelle Drive e permette di spostare
// i doppioni nel cestino, tenendo sempre il più vecchio (l'originale).
export default function DoppioniDrive({ clienteId = null, compatto = false }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState({});
  const [conferma, setConferma] = useState(false);
  const [pulendo, setPulendo] = useState(false);

  const cerca = async () => {
    setBusy(true); setRes(null); setSel({});
    try {
      const { data } = await api.get("/drive/doppioni", { params: clienteId ? { cliente_id: clienteId } : {} });
      setRes(data);
      const pre = {};
      data.risultati.forEach((r) => r.gruppi.forEach((g) => g.doppioni.forEach((d) => { pre[d.id] = true; })));
      setSel(pre);
      if (data.totale_doppioni === 0) toast.success("Nessun doppione trovato: le cartelle sono pulite.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ricerca non riuscita");
    } finally { setBusy(false); }
  };

  const scelti = Object.keys(sel).filter((k) => sel[k]);

  const elimina = async () => {
    setPulendo(true);
    try {
      const { data } = await api.post("/drive/doppioni/elimina", { file_ids: scelti });
      toast.success(`${data.eliminati} doppione/i spostato/i nel cestino di Drive (recuperabili 30 giorni)${data.falliti ? ` · ${data.falliti} non riusciti` : ""}`);
      setConferma(false);
      await cerca();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eliminazione non riuscita");
    } finally { setPulendo(false); }
  };

  return (
    <div data-testid="doppioni-drive" className={compatto ? "" : "mt-6 bg-white border border-[#1A2B4A]/10 rounded-sm p-4"}>
      <div className="flex items-center justify-between gap-4 mb-2">
        <div className="flex items-center gap-2">
          <Copy className="w-5 h-5 text-[#1A2B4A]" />
          <h3 className={compatto ? "text-sm font-semibold text-[#1A2B4A]" : "font-serif text-xl text-[#1A2B4A]"}>
            Doppioni su Drive
          </h3>
        </div>
        <button data-testid="doppioni-cerca" onClick={cerca} disabled={busy}
          className="inline-flex items-center gap-2 text-sm bg-[#1A2B4A] text-white px-4 py-2 rounded-sm hover:bg-[#22345c] disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
          {busy ? "Analisi in corso…" : "Cerca doppioni"}
        </button>
      </div>
      <p className="text-sm text-[#4a5568]">
        Confronta l'impronta digitale dei file: vengono segnalati solo quelli con <b>contenuto identico</b>.
        Il più vecchio viene sempre conservato.
      </p>

      {res && (
        <div className="mt-4" data-testid="doppioni-risultato">
          <p className="text-sm text-[#1A2B4A] mb-3">
            {res.totale_doppioni === 0
              ? `Nessun doppione su ${res.clienti_analizzati} cartella/e analizzata/e.`
              : `${res.totale_doppioni} doppione/i in ${res.risultati.length} cliente/i (su ${res.clienti_analizzati} analizzati).`}
          </p>
          {res.risultati.map((r) => (
            <div key={r.cliente_id} className="mb-4" data-testid={`doppioni-cliente-${r.cliente_id}`}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#8a6d1f] mb-1.5">{r.cliente}</p>
              {r.gruppi.map((g) => (
                <div key={g.md5} className="border border-[#1A2B4A]/10 rounded-sm p-3 mb-2">
                  <div className="flex items-center gap-2 text-xs text-[#276749] mb-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">Conservato: <b>{g.tieni.name}</b> · {g.tieni.categoria} · {dataBreve(g.tieni.modificato)} · {kb(g.tieni.size)}</span>
                  </div>
                  {g.doppioni.map((d) => (
                    <label key={d.id} data-testid={`doppione-${d.id}`}
                      className="flex items-center gap-2 text-xs text-[#1A2B4A] py-1 cursor-pointer">
                      <input type="checkbox" checked={!!sel[d.id]} onChange={() => setSel({ ...sel, [d.id]: !sel[d.id] })}
                        className="w-3.5 h-3.5 accent-[#C9A84C]" />
                      <span className="truncate flex-1">{d.name} · {d.categoria} · {dataBreve(d.modificato)}</span>
                      <a href={d.link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                        className="text-[#4a5568] hover:text-[#1A2B4A]"><ExternalLink className="w-3.5 h-3.5" /></a>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          ))}
          {res.totale_doppioni > 0 && (
            <button data-testid="doppioni-elimina" onClick={() => setConferma(true)} disabled={scelti.length === 0}
              className="inline-flex items-center gap-2 text-sm bg-[#9b2c2c] text-white px-4 py-2 rounded-sm hover:bg-[#7a2323] disabled:opacity-50">
              <Trash2 className="w-4 h-4" /> Sposta nel cestino {scelti.length} doppione/i
            </button>
          )}
        </div>
      )}

      {conferma && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" data-testid="doppioni-conferma">
          <div className="bg-white rounded-lg max-w-md w-full p-4">
            <h4 className="font-serif text-xl text-[#1A2B4A] mb-2">Confermi l'eliminazione?</h4>
            <p className="text-sm text-[#4a5568]">
              {scelti.length} file identici verranno spostati nel <b>cestino di Google Drive</b>, dove restano
              recuperabili per 30 giorni. Gli originali (i più vecchi) non vengono toccati.
            </p>
            <div className="flex justify-end gap-3 mt-5">
              <button data-testid="doppioni-annulla" onClick={() => setConferma(false)}
                className="text-sm text-[#4a5568] px-4 py-2 hover:text-[#1A2B4A]">Annulla</button>
              <button data-testid="doppioni-conferma-ok" onClick={elimina} disabled={pulendo}
                className="inline-flex items-center gap-2 text-sm bg-[#9b2c2c] text-white px-4 py-2 rounded-sm hover:bg-[#7a2323] disabled:opacity-50">
                {pulendo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Sì, elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
